import React, { useEffect, useRef, useState } from "react";
import { Step } from "../../types/steps";
import { ViewerSettings } from "../../types/settings";
import { t } from "../../utils/i18n";
import { withOpacity } from "../../utils/colorUtils";
type LanguageCode = ViewerSettings["language"];

const STEP_CONTROL_COPY: Record<
  LanguageCode,
  {
    previousStep: string;
    playAnimation: string;
    pauseAnimation: string;
    nextStep: string;
    expandInstructions: string;
    collapseInstructions: string;
  }
> = {
  en: {
    previousStep: "Previous step",
    playAnimation: "Play animation",
    pauseAnimation: "Pause animation",
    nextStep: "Next step",
    expandInstructions: "Show step instructions",
    collapseInstructions: "Hide step instructions",
  },
  es: {
    previousStep: "Paso anterior",
    playAnimation: "Reproducir animaci\u00f3n",
    pauseAnimation: "Pausar animaci\u00f3n",
    nextStep: "Siguiente paso",
    expandInstructions: "Mostrar instrucciones",
    collapseInstructions: "Ocultar instrucciones",
  },
  fr: {
    previousStep: "\u00c9tape pr\u00e9c\u00e9dente",
    playAnimation: "Lire l\u2019animation",
    pauseAnimation: "Mettre l\u2019animation en pause",
    nextStep: "\u00c9tape suivante",
    expandInstructions: "Afficher les instructions",
    collapseInstructions: "Masquer les instructions",
  },
  de: {
    previousStep: "Vorheriger Schritt",
    playAnimation: "Animation abspielen",
    pauseAnimation: "Animation anhalten",
    nextStep: "N\u00e4chster Schritt",
    expandInstructions: "Anweisungen anzeigen",
    collapseInstructions: "Anweisungen ausblenden",
  },
  zh: {
    previousStep: "\u4e0a\u4e00\u6b65",
    playAnimation: "\u64ad\u653e\u52a8\u753b",
    pauseAnimation: "\u6682\u505c\u52a8\u753b",
    nextStep: "\u4e0b\u4e00\u6b65",
    expandInstructions: "\u663e\u793a\u8bf4\u660e",
    collapseInstructions: "\u9690\u85cf\u8bf4\u660e",
  },
};

interface StepControlsProps {
  onPrevious?: () => void;
  onNext?: () => void;
  onPlayPause?: () => void;
  isPlaying?: boolean;
  language?: LanguageCode;
  /** Current step number (1-based) */
  currentStep?: number;
  /** Total number of steps */
  totalSteps?: number;
  /** Optional: Step title (deprecated - use steps array instead) */
  stepTitle?: string;
  /** Optional: Array of step data from API */
  steps?: Step[];
  showStepIndicator?: boolean;
  showProgressBar?: boolean;
  disablePrevious?: boolean;
  disableNext?: boolean;
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  textColor?: string;
  iconColor?: string;
  primaryButtonColor?: string;
  primaryButtonTextColor?: string;
  progressBarColor?: string;
  progressBarBackgroundColor?: string;
  fontFamily?: string;
  /**
   * Controlled instructions-panel state. When omitted the component
   * manages its own state internally (default: expanded).
   */
  instructionsExpanded?: boolean;
  onInstructionsExpandedChange?: (expanded: boolean) => void;
}

/**
 * Step controls system with two UX behaviors layered on top of the base
 * playback UI:
 *
 *  1. Auto-hide during playback. When `isPlaying` is true (i.e., the user
 *     has pressed prev/next/play and a step animation or transition is in
 *     progress), the entire control surface fades out and disables pointer
 *     events. As soon as playback stops, the controls fade back in. This
 *     keeps the 3D viewport unobstructed during transitions, especially
 *     on small screens.
 *
 *  2. Collapsible step instruction panel. The step indicator pill expands
 *     into an accordion containing the full step description. The
 *     accordion animates both height and opacity. Default is expanded;
 *     consumers can drive the state via `instructionsExpanded` and
 *     `onInstructionsExpandedChange` to keep multiple breakpoints in
 *     sync, otherwise the component manages it internally.
 *
 * Animation timings (consistent across all viewports):
 *   - controls fade:      280ms ease-in-out  (opacity + small Y drop)
 *   - accordion expand:   320ms cubic-bezier(0.16, 1, 0.3, 1)
 *   - accordion collapse: 220ms ease-in
 */
export function StepControls({
  onPrevious,
  onNext,
  onPlayPause,
  isPlaying = false,
  language = "en",
  currentStep,
  totalSteps,
  stepTitle,
  steps,
  showStepIndicator = true,
  showProgressBar = true,
  disablePrevious = false,
  disableNext = false,
  backgroundColor = "rgba(55, 55, 55, 0.85)",
  backdropBlur = "24px",
  borderColor = "rgba(255, 255, 255, 0.12)",
  boxShadow = "0 8px 32px 0 rgba(0, 0, 0, 0.4)",
  textColor = "rgba(255, 255, 255, 0.9)",
  iconColor = "rgba(255, 255, 255, 0.8)",
  primaryButtonColor = "rgb(255, 255, 255)",
  primaryButtonTextColor = "rgba(15, 23, 42, 0.95)",
  progressBarColor = "rgb(56, 189, 248)",
  progressBarBackgroundColor = "rgba(255, 255, 255, 0.1)",
  fontFamily = "Arial, sans-serif",
  instructionsExpanded,
  onInstructionsExpandedChange,
}: StepControlsProps) {
  const copy = STEP_CONTROL_COPY[language] ?? STEP_CONTROL_COPY.en;
  // Use steps array if provided, otherwise fall back to manual props
  const currentStepData =
    steps && currentStep
      ? steps.find((s) => s.stepNumber === currentStep)
      : null;
  const displayTitle =
    currentStepData?.description || currentStepData?.title || stepTitle;
  const displayTotalSteps = steps?.length || totalSteps;

  const hasStepInfo =
    currentStep !== undefined && displayTotalSteps !== undefined;
  const progressPercentage = hasStepInfo
    ? (currentStep / displayTotalSteps) * 100
    : 0;

  // ── Accordion state ──────────────────────────────────────────────
  // Accept controlled state from the parent so multiple consumers (mobile
  // + desktop layouts) stay in sync. Fall back to internal state when
  // uncontrolled. Default is expanded so users immediately see instructions.
  const [internalExpanded, setInternalExpanded] = useState(true);
  const expanded =
    instructionsExpanded !== undefined ? instructionsExpanded : internalExpanded;
  const handleToggleExpanded = () => {
    const next = !expanded;
    if (instructionsExpanded === undefined) {
      setInternalExpanded(next);
    }
    onInstructionsExpandedChange?.(next);
  };

  // Measure the accordion's natural height so we can animate height in
  // both directions without layout jumps. Avoids 'height: auto' transition
  // limitations.
  const instructionsContentRef = useRef<HTMLDivElement | null>(null);
  const [instructionsHeight, setInstructionsHeight] = useState<number>(0);

  useEffect(() => {
    const node = instructionsContentRef.current;
    if (!node) return;
    // Use ResizeObserver so the height tracks layout changes (text
    // translation, font load, viewport resize, language switch, etc.)
    const measure = () => setInstructionsHeight(node.scrollHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [displayTitle, language]);

  // ── Auto-hide during playback ────────────────────────────────────
  // The entire StepControls surface fades out while playback is active.
  // We keep the DOM mounted (so layout doesn't jump and the fade-in is
  // smooth) and disable pointer events while hidden.
  const isHidden = !!isPlaying;

  return (
    <div
      style={{
        position: "absolute",
        bottom: "calc(4rem + env(safe-area-inset-bottom, 0px))",
        left: "50%",
        transform: `translateX(-50%) translateY(${isHidden ? 12 : 0}px)`,
        zIndex: 50,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1.5rem",
        opacity: isHidden ? 0 : 1,
        pointerEvents: isHidden ? "none" : "auto",
        transition:
          "opacity 280ms ease-in-out, transform 280ms ease-in-out",
        willChange: "opacity, transform",
      }}
      aria-hidden={isHidden}
      data-step-controls-hidden={isHidden ? "true" : "false"}
      data-oid="9qmvj53"
    >
      {/* Step Indicator + collapsible instructions */}
      {showStepIndicator && hasStepInfo && (
        <div
          style={{
            backgroundColor: backgroundColor,
            backdropFilter: `blur(${backdropBlur})`,
            WebkitBackdropFilter: `blur(${backdropBlur})`,
            border: `1px solid ${borderColor}`,
            boxShadow: boxShadow,
            borderRadius: "1.25rem",
            maxWidth: "min(42rem, calc(100vw - 2rem))",
            color: textColor,
            fontFamily,
            overflow: "hidden",
            // Animate the container's color/border when content reflows.
            transition: "border-radius 200ms ease-in-out",
          }}
          data-oid="m-b_tlb"
        >
          {/* Header row: step counter + chevron toggle */}
          <button
            type="button"
            onClick={displayTitle ? handleToggleExpanded : undefined}
            aria-expanded={displayTitle ? expanded : undefined}
            aria-controls={
              displayTitle ? "step-instructions-content" : undefined
            }
            aria-label={
              displayTitle
                ? expanded
                  ? copy.collapseInstructions
                  : copy.expandInstructions
                : undefined
            }
            disabled={!displayTitle}
            style={{
              all: "unset",
              boxSizing: "border-box",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.75rem",
              padding: "0.625rem 1.25rem",
              cursor: displayTitle ? "pointer" : "default",
              fontSize: "0.875rem",
              fontWeight: 500,
              color: textColor,
              fontFamily,
              letterSpacing: "0.01em",
              lineHeight: 1.35,
              minHeight: "2.75rem",
              // Touch-friendly tap target on mobile
              WebkitTapHighlightColor: "transparent",
            }}
            data-oid="step-indicator-header"
          >
            <span style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}>
              {t(language, "step.label")}{" "}
              {currentStep.toString().padStart(2, "0")}{" "}
              {t(language, "step.of")} {displayTotalSteps}
            </span>
            {displayTitle && currentStepData?.title ? (
              <span
                style={{
                  flex: "1 1 auto",
                  textAlign: "left",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  opacity: 0.85,
                  // Hide the inline title preview when expanded — the full
                  // text appears in the accordion body below.
                  visibility: expanded ? "hidden" : "visible",
                }}
              >
                {currentStepData.title}
              </span>
            ) : null}
            {displayTitle ? (
              <span
                className="material-symbols-outlined"
                aria-hidden
                style={{
                  fontSize: "1.25rem",
                  flex: "0 0 auto",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 240ms cubic-bezier(0.16, 1, 0.3, 1)",
                }}
              >
                expand_more
              </span>
            ) : null}
          </button>

          {/* Accordion body */}
          {displayTitle ? (
            <div
              id="step-instructions-content"
              role="region"
              aria-hidden={!expanded}
              style={{
                maxHeight: expanded ? instructionsHeight : 0,
                opacity: expanded ? 1 : 0,
                overflow: "hidden",
                transition: expanded
                  ? "max-height 320ms cubic-bezier(0.16, 1, 0.3, 1), opacity 320ms cubic-bezier(0.16, 1, 0.3, 1)"
                  : "max-height 220ms ease-in, opacity 180ms ease-in",
                willChange: "max-height, opacity",
              }}
              data-oid="step-instructions-body"
            >
              <div
                ref={instructionsContentRef}
                style={{
                  padding: "0 1.25rem 0.875rem 1.25rem",
                  fontSize: "0.875rem",
                  lineHeight: 1.45,
                  color: textColor,
                  fontFamily,
                  textAlign: "left",
                }}
              >
                {displayTitle}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Controls Container */}
      <div
        style={{
          backgroundColor: backgroundColor,
          backdropFilter: `blur(${backdropBlur})`,
          WebkitBackdropFilter: `blur(${backdropBlur})`,
          border: `1px solid ${borderColor}`,
          boxShadow: boxShadow,
          borderRadius: "9999px",
          padding: "1rem 2rem",
          display: "flex",
          alignItems: "center",
          gap: "2rem",
        }}
        data-oid="ab9dxcs"
      >
        {/* Previous Button */}
        <button
          onClick={onPrevious}
          disabled={disablePrevious}
          aria-label={copy.previousStep}
          style={{
            width: "2.5rem",
            height: "2.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "0.75rem",
            background: "transparent",
            border: "none",
            color: iconColor,
            cursor: disablePrevious ? "not-allowed" : "pointer",
            transition: "all 0.3s",
            opacity: disablePrevious ? 0.3 : 1,
          }}
          onMouseEnter={(e) => {
            if (!disablePrevious) {
              e.currentTarget.style.background = progressBarBackgroundColor;
              e.currentTarget.style.color = primaryButtonTextColor;
            }
          }}
          onMouseLeave={(e) => {
            if (!disablePrevious) {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = iconColor;
            }
          }}
          onMouseDown={(e) => {
            if (!disablePrevious) {
              e.currentTarget.style.transform = "scale(0.9)";
            }
          }}
          onMouseUp={(e) => {
            if (!disablePrevious) {
              e.currentTarget.style.transform = "scale(1)";
            }
          }}
          data-oid="oeljis:"
        >
          <span
            className="material-symbols-outlined"
            style={{
              fontSize: "1.875rem",
              fontWeight: 300,
            }}
            data-oid="5ttsojx"
          >
            chevron_left
          </span>
        </button>

        {/* Play/Pause Button */}
        <button
          onClick={onPlayPause}
          aria-label={isPlaying ? copy.pauseAnimation : copy.playAnimation}
          style={{
            width: "3.5rem",
            height: "3.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "9999px",
            background: primaryButtonColor,
            border: "none",
            color: primaryButtonTextColor,
            cursor: "pointer",
            transition: "all 0.3s",
            boxShadow: `0 0 25px ${borderColor}`,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = "scale(0.95)";
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
          data-oid="a97qytc"
        >
          <span
            className="material-symbols-outlined"
            style={{
              fontSize: "1.875rem",
              fontWeight: isPlaying ? 300 : 400,
            }}
            data-oid="5cf71_x"
          >
            {isPlaying ? "pause" : "play_arrow"}
          </span>
        </button>

        {/* Next Button */}
        <button
          onClick={onNext}
          disabled={disableNext}
          aria-label={copy.nextStep}
          style={{
            width: "2.5rem",
            height: "2.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "0.75rem",
            background: "transparent",
            border: "none",
            color: iconColor,
            cursor: disableNext ? "not-allowed" : "pointer",
            transition: "all 0.3s",
            opacity: disableNext ? 0.3 : 1,
          }}
          onMouseEnter={(e) => {
            if (!disableNext) {
              e.currentTarget.style.background = progressBarBackgroundColor;
              e.currentTarget.style.color = primaryButtonTextColor;
            }
          }}
          onMouseLeave={(e) => {
            if (!disableNext) {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = iconColor;
            }
          }}
          onMouseDown={(e) => {
            if (!disableNext) {
              e.currentTarget.style.transform = "scale(0.9)";
            }
          }}
          onMouseUp={(e) => {
            if (!disableNext) {
              e.currentTarget.style.transform = "scale(1)";
            }
          }}
          data-oid="nzl3a4b"
        >
          <span
            className="material-symbols-outlined"
            style={{
              fontSize: "1.875rem",
              fontWeight: 300,
            }}
            data-oid="0.ugj36"
          >
            chevron_right
          </span>
        </button>
      </div>

      {/* Progress Bar */}
      {showProgressBar && hasStepInfo && (
        <div
          style={{
            width: "18rem",
            maxWidth: "calc(100vw - 2rem)",
            height: "0.25rem",
            backgroundColor: progressBarBackgroundColor,
            borderRadius: "9999px",
            overflow: "hidden",
            position: "relative",
          }}
          data-oid="_ivz886"
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: "100%",
              width: `${progressPercentage}%`,
              backgroundColor: progressBarColor,
              borderRadius: "9999px",
              transition: "width 0.3s ease-in-out",
              boxShadow: `0 0 15px ${withOpacity(progressBarColor, 0.4)}`,
            }}
            data-oid="dsqokoj"
          />
        </div>
      )}
    </div>
  );
}
